import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Users, Shield, Smartphone, Home as HomeIcon, MessageCircle, Play, ChevronRight, Sparkles, Mic, UtensilsCrossed, ShoppingBasket, Receipt, Gamepad2, HeartHandshake, Languages } from 'lucide-react';
import SEO from '@/components/SEO';
import DigiMitraBookingForm from '@/components/DigiMitraBookingForm';
import NeighbourhoodInviteModal from '@/components/NeighbourhoodInviteModal';
import MediaCoverage from '@/components/MediaCoverage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VC_SERVICE_ICONS = [UtensilsCrossed, ShoppingBasket, Receipt, Gamepad2, HeartHandshake];

const VC_CONTENT = {
  en: {
    toggleLabel: 'हिंदी',
    badge: 'WhatsApp Voice Notes',
    title: "Elderra's Voice Companion",
    lead: 'Now just send voice notes to:',
    services: [
      'Order food',
      'Buy groceries',
      'Pay electricity & water bills',
      'Play memory-boosting games',
      'Make new friends',
    ],
    examplesTitle: 'Just say it — here is how it works',
    voiceNoteLabel: 'Voice note',
    examples: [
      { note: 'Order 1 kg atta', reply: 'Done! 1 kg atta added from your usual store. Delivery by 6 PM today. 🛒' },
      { note: 'Pay my electricity bill', reply: 'Your bill of ₹842 is ready. Shall I pay it now? Just say yes. ⚡' },
      { note: 'I want to play a memory game', reply: "Let's play! Repeat these 3 words back to me: Rose, Clock, River. 🌹" },
      { note: 'I feel a little lonely today', reply: "I'm here with you. Would you like me to connect you to a friendly group in your city? 💬" },
    ],
    watch: 'Watch Demo',
    cta: 'Try it on WhatsApp',
  },
  hi: {
    toggleLabel: 'English',
    badge: 'व्हाट्सऐप वॉइस नोट्स',
    title: 'एल्डेरा वॉइस कंपेनियन',
    lead: 'अब बस वॉइस नोट भेजें:',
    services: [
      'खाना ऑर्डर करें',
      'किराना सामान खरीदें',
      'बिजली और पानी के बिल भरें',
      'याददाश्त बढ़ाने वाले खेल खेलें',
      'नए दोस्त बनाएं',
    ],
    examplesTitle: 'बस बोलिए — ऐसे काम करता है',
    voiceNoteLabel: 'वॉइस नोट',
    examples: [
      { note: '1 किलो आटा ऑर्डर करो', reply: 'हो गया! आपकी दुकान से 1 किलो आटा जोड़ दिया। आज शाम 6 बजे तक डिलीवरी। 🛒' },
      { note: 'मेरा बिजली का बिल भर दो', reply: 'आपका ₹842 का बिल तैयार है। क्या मैं अभी भर दूं? बस हाँ कहें। ⚡' },
      { note: 'मुझे एक याददाश्त का खेल खेलना है', reply: 'चलिए खेलते हैं! ये 3 शब्द मुझे दोहराइए: गुलाब, घड़ी, नदी। 🌹' },
      { note: 'आज मैं थोड़ा अकेला महसूस कर रहा हूँ', reply: 'मैं आपके साथ हूँ। क्या मैं आपको आपके शहर के एक दोस्ताना समूह से जोड़ दूं? 💬' },
    ],
    watch: 'डेमो देखें',
    cta: 'व्हाट्सऐप पर आज़माएं',
  },
};

const Home = () => {
  const [showDigiMitraModal, setShowDigiMitraModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [vcLang, setVcLang] = useState('en');
  const vc = VC_CONTENT[vcLang];
  const [stats, setStats] = useState({
    users: 1500,
    groups: 50,
    cities: 50,
    societies: 100
  });

  // Slideshow auto-play effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 5); // 5 images total
    }, 7000); // Change every 7 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="page-container">
      <SEO 
        title="Elderra | Bridging tech for the Elderly"
        description="Empowering 1500+ senior citizens across 50+ Indian cities with digital literacy. Learn cyber safety, avoid online scams, and navigate technology safely. I am 70+ and I don't share OTPs. Join free training programs, DigiMitra home visits, and WhatsApp support."
        keywords="I am 70+, don't share OTP, elderly digital training, senior citizens cyber safety, digital literacy India, online scam prevention seniors, DigiMitra home visits, Elderra tech training, WhatsApp support elderly"
        ogImage="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/4owwqeky_Elderra%20Banner.png"
        ogUrl="https://elderra.io"
      />
      {/* Hero Section */}
      <section className="hero-home">
        <div className="hero-overlay-home"></div>
        <div className="hero-content-home">
          <h1 className="hero-title-home" data-testid="hero-title">
            Empowering India's Elders in the digital age
          </h1>
          <p className="hero-subtitle-home">
            One WhatsApp-based assistant that aggregates apps and services relevant for elderly population, along with hands-on support.
          </p>
          <div className="hero-buttons">
            <Link to="/donate">
              <Button 
                className="btn-primary-large" 
                size="lg"
                data-testid="support-movement-btn"
              >
                <Users className="btn-icon" />
                Support the Movement
              </Button>
            </Link>
            <Button 
              className="btn-secondary-large" 
              size="lg"
              variant="outline"
              onClick={() => setShowDigiMitraModal(true)}
              data-testid="book-digimitra-btn"
            >
              <HomeIcon className="btn-icon" />
              Book DigiMitra Home Visit
            </Button>
            <Link to="/community">
              <Button 
                className="btn-secondary-large" 
                size="lg"
                variant="outline"
                data-testid="join-community-btn"
              >
                <Users className="btn-icon" />
                Join Community
              </Button>
            </Link>
            <Button
              className="btn-secondary-large"
              size="lg"
              variant="outline"
              onClick={() => setShowInviteModal(true)}
              data-testid="invite-neighbourhood-btn"
            >
              <Sparkles className="btn-icon" />
              Invite us to your neighbourhood
            </Button>
          </div>
        </div>
      </section>

      {/* Elderra's Voice Companion */}
      <section className="vc-home" data-testid="voice-companion-section">
        <div className="container">
          <div className="vc-home-topbar">
            <span className="vc-home-badge"><MessageCircle size={16} /> {vc.badge}</span>
            <button
              type="button"
              className="vc-lang-toggle"
              onClick={() => setVcLang((l) => (l === 'en' ? 'hi' : 'en'))}
              data-testid="voice-companion-lang-toggle"
              aria-label="Switch language"
            >
              <Languages size={16} /> {vc.toggleLabel}
            </button>
          </div>
          <div className="vc-home-inner">
            <div className="vc-home-text">
              <h2 className="vc-home-title" data-testid="voice-companion-title">{vc.title}</h2>
              <p className="vc-home-lead">{vc.lead}</p>
              <ul className="vc-home-services" data-testid="voice-companion-uses">
                {vc.services.map((label, i) => {
                  const Icon = VC_SERVICE_ICONS[i];
                  return (
                    <li className="vc-service-item" key={i} data-testid={`voice-companion-service-${i}`}>
                      <span className="vc-service-icon"><Icon size={20} /></span>
                      {label}
                    </li>
                  );
                })}
              </ul>
              <a
                href="https://wa.me/919205070300?text=Hi%20Elderra%2C%20I%27d%20like%20to%20try%20the%20Voice%20Companion"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="voice-companion-section-cta"
              >
                <Button className="btn-primary-large" size="lg">
                  <MessageCircle className="btn-icon" /> {vc.cta}
                </Button>
              </a>
            </div>
            <div className="vc-home-video">
              <p className="vc-home-watch"><Play size={18} /> {vc.watch}</p>
              <div className="vc-home-frame">
                <iframe
                  src="https://www.youtube.com/embed/0Px6jUueTPw"
                  title="Elderra's Voice Companion Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  data-testid="voice-companion-video"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTuber Program Banner */}
      <section className="yt-home-banner" data-testid="yt-home-banner">
        <div className="container">
          <div className="yt-banner-inner">
            <div className="yt-banner-content">
              <div className="yt-banner-badge">New Program</div>
              <h2>Elderra YouTuber Program</h2>
              <p>Helping 100 inspiring people above 60 start their YouTube channels. First visit <strong>absolutely free of cost</strong>. Currently offering in Delhi NCR.</p>
              <Link to="/youtuber-program">
                <Button className="yt-banner-btn" data-testid="yt-home-cta">
                  Learn More & Sign Up <ChevronRight size={18} />
                </Button>
              </Link>
            </div>
            <div className="yt-banner-visual">
              <Play size={64} />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="impact-stats-section">
        <div className="container">
          <h2 className="section-title-center">Impact So Far</h2>
          <div className="impact-content-wrapper">
            <div className="impact-image-container">
              <div className="testimonial-slideshow">
                <img 
                  key={currentSlide}
                  src={[
                    "https://customer-assets.emergentagent.com/job_cyber-elders/artifacts/mmmf1cug_Screen%20Shot%202025-11-21%20at%201.07.59%20PM.png",
                    "https://customer-assets.emergentagent.com/job_cyber-elders/artifacts/etmo1d0n_DSC02625%20%282%29.jpg",
                    "https://customer-assets.emergentagent.com/job_cyber-elders/artifacts/b2xumxz1_DSC02630%20%281%29.jpg",
                    "https://customer-assets.emergentagent.com/job_cyber-elders/artifacts/j1aovly1_DSC02632%20%281%29%20%281%29.jpg",
                    "https://customer-assets.emergentagent.com/job_cyber-elders/artifacts/ghmkpsyc_Kochi%201.jpg"
                  ][currentSlide]}
                  alt={`Elderra community member testimonial ${currentSlide + 1}`}
                  className="slideshow-image-single"
                />
              </div>
            </div>
            <div className="impact-stats-grid">
              <div className="impact-stat-box">
                <div className="impact-stat-number">1,500+</div>
                <div className="impact-stat-label">Elders trained and secured against scams</div>
              </div>
              <div className="impact-stat-box highlight-stat">
                <div className="impact-stat-number">0</div>
                <div className="impact-stat-label">Community members scammed after training</div>
              </div>
              <div className="impact-stat-box">
                <div className="impact-stat-number">50+</div>
                <div className="impact-stat-label">WhatsApp Groups</div>
              </div>
              <div className="impact-stat-box">
                <div className="impact-stat-number">100+</div>
                <div className="impact-stat-label">Societies and Clubs onboarded</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <MediaCoverage />

      {/* Problem Statement */}
      <section className="problem-section">
        <div className="container">
          <h2 className="section-title-center">The Digital Divide Challenge</h2>
          <p className="section-subtitle-center">
            150 million Indians aged 55+ struggle with basic tech skills. Digital exclusion is causing isolation and vulnerability.
          </p>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">📱</div>
              <h3>Overwhelming Technology</h3>
              <p>Every app has unique design. Too much to learn, causing digital exclusion.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">👨‍👩‍👧‍👦</div>
              <h3>Over-Reliance on Others</h3>
              <p>Elderly depend on younger relatives for essential tasks, creating knowledge gaps.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">🚨</div>
              <h3>Vulnerability to Scams</h3>
              <p>Knowledge gaps place them at high risk. Real, fake, deepfake - everything looks the same.</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">🏦</div>
              <h3>Traditional Avenues Disappearing</h3>
              <p>Banking, shopping, utilities - everything going digital, leaving elders behind.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="container">
          <h2 className="section-title-center">How Elderra Helps</h2>
          <div className="services-grid-home">
            <div className="service-card-home">
              <Smartphone className="service-icon" size={48} />
              <h3>Elderra Voice Companion (Being Tested)</h3>
              <p>No app downloads needed. Talk like you're speaking to your grandchildren. Order food, book cabs, pay bills - all through WhatsApp.</p>
              <a
                href="https://wa.me/919205070300?text=Hi%20Elderra%2C%20I%27d%20like%20to%20try%20the%20Voice%20Companion"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-voice-companion-learn-more"
              >
                <Button variant="link" className="learn-more-btn">Chat on WhatsApp →</Button>
              </a>
            </div>
            <div className="service-card-home featured">
              <HomeIcon className="service-icon" size={48} />
              <h3>DigiMitra Home Visits</h3>
              <p><strong>₹499 per hour</strong> - Verified support for onboarding, troubleshooting, training, and concierge services at your home.</p>
              <Button 
                className="btn-primary" 
                onClick={() => setShowDigiMitraModal(true)}
                data-testid="book-visit-btn"
              >
                Book Now
              </Button>
            </div>
            <div className="service-card-home">
              <Users className="service-icon" size={48} />
              <h3>Community Meetups</h3>
              <p>4-hour programs covering fraud prevention, app training, and social connection in 50+ cities across India.</p>
              <Link to="/community">
                <Button variant="link" className="learn-more-btn">Find Your City →</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="video-section">
        <div className="container">
          <h2 className="section-title-center">See Elderra in Action</h2>
          <p className="section-subtitle-center">
            Watch how we're helping India's seniors navigate the digital world
          </p>
          <div className="video-container">
            <iframe
              width="100%"
              height="500"
              src="https://www.youtube.com/embed/R7ZKv7Cj4-Y"
              title="Elderra Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="video-iframe"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="partners-section">
        <div className="container">
          <h2 className="section-title-center">Our Partners</h2>
          <p className="section-subtitle-center">
            Working together to empower India's seniors
          </p>
          <div className="partners-grid">
            <a 
              href="https://elevenlabs.io/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="partner-logo-link"
              data-testid="partner-elevenlabs"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_elderra-crowdfund/artifacts/j3jkv79k_elevenlabs-logo-black-png.png" 
                alt="ElevenLabs"
                className="partner-logo"
              />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Join Us in Making the Internet Accessible</h2>
            <p>Help us empower India's senior population to navigate the digital world safely and independently.</p>
            <div className="cta-buttons">
              <Link to="/donate">
                <Button className="btn-primary-large" size="lg">
                  Support Our Mission
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary-large" size="lg" variant="outline">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DigiMitra Booking Modal */}
      <DigiMitraBookingForm open={showDigiMitraModal} onOpenChange={setShowDigiMitraModal} />
      <NeighbourhoodInviteModal open={showInviteModal} onOpenChange={setShowInviteModal} />
    </div>
  );
};

export default Home;